/**
 * セッション認証サービス
 * URL の session_key を検証し、セッション API 応答を App 全体の実行コンテキストへ反映する
 */

import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, lastValueFrom } from 'rxjs';
import { BizFrontendApiService, IsEnvironmentService } from '@interserve/angular';
import { environment } from '../../../environments/environment';
import { AppConfigService } from '../config/app-config.service';
import { InspectionAccessStore } from './inspection-access.store';
import { InspectionLinkService } from './inspection-link.service';
import { RouteModeLockStore } from './route-mode-lock.store';
import { SessionContext, SessionContextStore } from './session-context.store';
import {
  formatUtcDateTimeForDisplay,
  hasSameUtcInstant,
  normalizeUtcDateTimeString,
} from './session-time.util';

interface SessionApiResponseData {
  reporterId: string;
  userName: string;
  expiresAt: string;
}

interface SessionApiResponse {
  resultCode?: number;
  resultMessage?: string;
  data?: SessionApiResponseData | null;
}

/** 認証ブートストラップ画面に表示する状態 */
export interface SessionBootstrapState {
  /** 説明メッセージ */
  message: string;

  /** エラー表示かどうか */
  isError: boolean;
}

@Injectable({ providedIn: 'root' })
export class SessionAuthService {
  private static readonly sessionKeyQueryNames = ['session_key', 'sessionkey'] as const;
  private static readonly uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

  private appInitializationPromise: Promise<boolean> | null = null;

  private readonly bootstrapStateSubject = new BehaviorSubject<SessionBootstrapState>({
    message: 'セッション情報を確認しています...',
    isError: false,
  });

  /** 起動中メッセージの購読用 Observable */
  readonly bootstrapState$: Observable<SessionBootstrapState> = this.bootstrapStateSubject.asObservable();

  /** inspection で session の expiresAt も併せて検証するか */
  private get enforceInspectionSessionExpiryCheck(): boolean {
    return environment.inspectionLink.enforceSessionExpiryCheck;
  }

  constructor(
    private readonly bizFrontendApi: BizFrontendApiService,
    private readonly environmentService: IsEnvironmentService,
    private readonly appConfig: AppConfigService,
    private readonly sessionStore: SessionContextStore,
    private readonly inspectionAccessStore: InspectionAccessStore,
    private readonly inspectionLinks: InspectionLinkService,
    private readonly routeModeLock: RouteModeLockStore,
  ) {}

  /** アプリ開始前に session_key を検証し、実行コンテキストを確定する */
  async initialize(appCode: string): Promise<void> {
    await this.initializeApplication(appCode);

    if (this.inspectionLinks.isInspectionRoute()) {
      await this.initializeInspectionRoute();
      return;
    }

    const sessionKey = this.getSessionKeyFromUrl();
    if (!sessionKey) {
      await this.restoreStoredSessionContext();
      return;
    }

    if (!this.isValidSessionKey(sessionKey)) {
      this.redirectForInvalidSession('URL に有効な session_key が含まれていません。', {
        acceptedQueryNames: SessionAuthService.sessionKeyQueryNames,
        sessionKey,
      });
    }

    const sessionContext = await this.fetchSessionContext(sessionKey);
    await this.validateSessionContext(sessionKey, sessionContext);

    this.bootstrapStateSubject.next({
      message: 'セッション情報を反映しています...',
      isError: false,
    });
    this.sessionStore.upsertSession(sessionContext);
    this.persistWorkbenchRouteModeLock();
    this.appConfig.saveSettings({ reporterId: sessionContext.reporterId });
    this.cleanupSessionKeyFromUrl();
  }

  /** inspection 共有 URL からの起動フローを処理する */
  private async initializeInspectionRoute(): Promise<void> {
    const sessionKey = this.getSessionKeyFromUrl();
    const accessToken = this.inspectionLinks.getAccessTokenFromUrl();
    const tabId = this.inspectionLinks.getTabIdFromUrl();
    const viewCd = this.inspectionLinks.getViewCdFromUrl();

    if (sessionKey || accessToken) {
      if (!sessionKey || !accessToken || !tabId) {
        this.redirectForInvalidSession('inspection URL に必要な情報が不足しています。');
      }

      if (!this.isValidSessionKey(sessionKey)) {
        this.redirectForInvalidSession('inspection URL の session_key が不正です。', { sessionKey });
      }

      const inspectionContext = this.inspectionLinks.buildContextFromUrl(sessionKey, tabId, viewCd, accessToken);
      if (!inspectionContext) {
        this.redirectForInvalidSession('inspection URL の有効期限情報を解釈できません。', { accessToken });
      }

      if (this.inspectionLinks.isExpired(inspectionContext)) {
        this.redirectForInvalidSession('inspection URL の有効期限が切れています。', inspectionContext);
      }

      const sessionContext = await this.fetchSessionContext(sessionKey);
      await this.validateSessionContext(sessionKey, sessionContext, this.enforceInspectionSessionExpiryCheck);

      this.bootstrapStateSubject.next({
        message: 'inspection URL の認証情報を反映しています... ',
        isError: false,
      });
      this.sessionStore.upsertSession(sessionContext);
      this.inspectionAccessStore.upsert(inspectionContext);
      this.routeModeLock.setMode('inspection');
      this.appConfig.saveSettings({ reporterId: sessionContext.reporterId });
      this.cleanupInspectionSensitiveQueryParams();
      this.inspectionLinks.ensureInspectionQueryParams(inspectionContext);
      return;
    }

    const storedInspectionContext = this.inspectionAccessStore.snapshot;
    if (!storedInspectionContext) {
      this.redirectForInvalidSession('inspection URL の再利用に必要な認証情報が見つかりません。');
    }

    if (this.inspectionAccessStore.isExpired(storedInspectionContext)) {
      this.redirectForInvalidSession('保存済み inspection URL の有効期限が切れています。', storedInspectionContext);
    }

    await this.restoreStoredInspectionContext(storedInspectionContext);
    this.inspectionLinks.ensureInspectionQueryParams(storedInspectionContext);
  }

  /** 現在のセッション状態を返す */
  get currentSession(): SessionContext | null {
    return this.sessionStore.snapshot;
  }

  /** BizFrontend ライブラリ初期化を一度だけ実行する */
  async initializeApplication(appCode: string): Promise<boolean> {
    if (!this.appInitializationPromise) {
      this.appInitializationPromise = this.runApplicationInitialization(appCode);
    }

    return this.appInitializationPromise;
  }

  /** frontendCd に紐づく View 定義本体を取得する */
  async getFrontendDefinition<TDefinition>(frontendCode: string): Promise<TDefinition> {
    return await lastValueFrom(this.bizFrontendApi.getAppVersionFrontends(frontendCode)) as TDefinition;
  }

  /** Environment 設定を BizFrontend 側へ反映する */
  private initializeEnvironment(appCode: string): void {
    this.environmentService.setAppSettings({
      appCd: appCode,
      environmentKey: environment.production ? 'proSche_prod' : 'dev',
    });
  }

  /** BizFrontend アプリ初期化本体を実行する */
  private async runApplicationInitialization(appCode: string): Promise<boolean> {
    this.initializeEnvironment(appCode);

    const initialized = await lastValueFrom(this.bizFrontendApi.initializeApp(appCode, undefined, []));
    if (!initialized) {
      this.redirectForInvalidSession('アプリケーション初期化に失敗しました。');
    }

    return initialized;
  }

  /** URL から session_key を取得する */
  private getSessionKeyFromUrl(): string {
    const params = new URLSearchParams(window.location.search);

    for (const queryName of SessionAuthService.sessionKeyQueryNames) {
      const sessionKey = params.get(queryName)?.trim();
      if (sessionKey) {
        return sessionKey;
      }
    }

    return '';
  }

  /** session_key 形式が UUID として妥当かを判定する */
  private isValidSessionKey(sessionKey: string): boolean {
    return SessionAuthService.uuidPattern.test(sessionKey);
  }

  /** セッション API から認証コンテキストを取得する */
  private async fetchSessionContext(sessionKey: string): Promise<SessionContext> {
    let sessionResponse: SessionApiResponse;

    try {
      sessionResponse = await lastValueFrom(this.bizFrontendApi.getSession(sessionKey)) as SessionApiResponse;
    } catch (error) {
      this.redirectForInvalidSession('セッション取得 API の呼び出しに失敗しました。', error);
    }

    const responseData = sessionResponse.data;
    if (!responseData || !responseData.reporterId || !responseData.expiresAt) {
      this.redirectForInvalidSession('セッション取得結果に必要な情報が含まれていません。', sessionResponse);
    }

    return {
      sessionKey,
      reporterId: responseData.reporterId,
      userName: responseData.userName ?? '',
      expiresAt: this.normalizeSessionExpiry(responseData.expiresAt, sessionResponse),
    };
  }

  /** 取得したセッション情報と保存済み状態を検証する */
  private async validateSessionContext(
    sessionKey: string,
    sessionContext: SessionContext,
    enforceExpiryCheck = true,
  ): Promise<void> {
    if (enforceExpiryCheck && this.sessionStore.isExpired(sessionContext)) {
      await this.redirectForExpiredSession(sessionContext);
    }

    const storedContext = this.sessionStore.snapshot;
    if (
      storedContext
      && this.sessionStore.isSameSession(sessionKey)
      && (
        storedContext.reporterId !== sessionContext.reporterId
        || !hasSameUtcInstant(storedContext.expiresAt, sessionContext.expiresAt)
      )
    ) {
      console.error('[SessionAuthService] 保存済みセッション情報と取得結果が一致しません。', {
        storedContext,
        sessionContext,
      });
      this.redirectForInvalidSession('保存済みセッション情報と取得結果が一致しません。');
    }
  }

  /**
   * URL に session_key が無い場合は、保存済みセッションをサーバーへ再照会して継続可否を判定する
   * localStorage の有効期限改ざんを防ぐため、最終的にはサーバー応答を正とする
   */
  private async restoreStoredSessionContext(): Promise<void> {
    const storedContext = this.sessionStore.snapshot;
    if (!storedContext) {
      this.redirectForInvalidSession('URL に session_key が無く、再利用できる認証情報も存在しません。');
    }

    if (this.sessionStore.isExpired(storedContext)) {
      await this.redirectForExpiredSession(storedContext);
    }

    this.bootstrapStateSubject.next({
      message: '保存済みセッション情報をサーバーへ照会しています...',
      isError: false,
    });

    const refreshedContext = await this.fetchSessionContext(storedContext.sessionKey);
    await this.validateStoredSessionContext(storedContext, refreshedContext);

    this.bootstrapStateSubject.next({
      message: '保存済みセッション情報を再利用しています...',
      isError: false,
    });

    this.sessionStore.upsertSession(refreshedContext);
    this.persistWorkbenchRouteModeLock();
    this.appConfig.saveSettings({ reporterId: refreshedContext.reporterId });
  }

  /** 保存済みセッション情報を明示的に破棄する */
  private clearStoredSessionContext(): void {
    this.sessionStore.clear();
    this.inspectionAccessStore.clear();
    this.routeModeLock.clear();
    this.appConfig.saveSettings({ reporterId: '' });
  }

  /** workbench 画面での認証完了時だけ route mode lock を更新する */
  private persistWorkbenchRouteModeLock(pathname: string = window.location.pathname): void {
    if (!this.isWorkbenchRoute(pathname)) {
      return;
    }

    this.routeModeLock.setMode('workbench');
  }

  /** 保存済み inspection 情報を使ってセッションを再構築する */
  private async restoreStoredInspectionContext(storedInspectionContext: { sessionKey: string; expiresAt: string }): Promise<void> {
    this.bootstrapStateSubject.next({
      message: '保存済み inspection 認証情報を再利用しています... ',
      isError: false,
    });

    const refreshedContext = await this.fetchSessionContext(storedInspectionContext.sessionKey);
    const storedSessionContext = this.sessionStore.snapshot;
    if (storedSessionContext && storedSessionContext.sessionKey === storedInspectionContext.sessionKey) {
      await this.validateStoredSessionContext(
        storedSessionContext,
        refreshedContext,
        this.enforceInspectionSessionExpiryCheck,
      );
    } else if (this.enforceInspectionSessionExpiryCheck && this.sessionStore.isExpired(refreshedContext)) {
      await this.redirectForExpiredSession(refreshedContext);
    }

    this.sessionStore.upsertSession(refreshedContext);
    this.routeModeLock.setMode('inspection');
    this.appConfig.saveSettings({ reporterId: refreshedContext.reporterId });
  }

  /** expiresAt を UTC ISO 文字列へ正規化し、曖昧な時差解釈を排除する */
  private normalizeSessionExpiry(expiresAt: string, detail?: unknown): string {
    const normalizedExpiresAt = normalizeUtcDateTimeString(expiresAt);
    if (!normalizedExpiresAt) {
      this.redirectForInvalidSession('セッションの有効期限形式を解釈できませんでした。', {
        detail,
        expiresAt,
      });
    }

    return normalizedExpiresAt;
  }

  /**
   * 保存済みセッションとサーバー再取得結果を比較し、再利用可否を判定する
   * reporterId が変わっていた場合は不正な状態として扱い、expiresAt はサーバー値で更新する
   */
  private async validateStoredSessionContext(
    storedContext: SessionContext,
    refreshedContext: SessionContext,
    enforceExpiryCheck = true,
  ): Promise<void> {
    if (enforceExpiryCheck && this.sessionStore.isExpired(refreshedContext)) {
      await this.redirectForExpiredSession(refreshedContext);
    }

    if (storedContext.reporterId !== refreshedContext.reporterId) {
      console.error('[SessionAuthService] 保存済み reporterId とサーバー結果が一致しません。', {
        storedContext,
        refreshedContext,
      });
      this.clearStoredSessionContext();
      this.redirectForInvalidSession('保存済み reporterId とサーバー結果が一致しません。');
    }
  }

  private isWorkbenchRoute(pathname: string): boolean {
    return this.normalizePath(pathname) === '/workbench';
  }

  private normalizePath(pathname: string): string {
    if (!pathname) {
      return '/';
    }

    const normalized = pathname.startsWith('/') ? pathname : `/${pathname}`;
    return normalized.length > 1 && normalized.endsWith('/')
      ? normalized.slice(0, -1)
      : normalized;
  }

  /** 無効セッション時に状態を破棄し、外部画面へ遷移する */
  private redirectForInvalidSession(message: string, detail?: unknown): never {
    console.error(`[SessionAuthService] ${message}`, detail);
    this.bootstrapStateSubject.next({ message, isError: true });
    this.clearStoredSessionContext();
    void this.redirectAfterDelay(0);
    throw new Error(message);
  }

  /**
   * 期限切れセッション時に通知文を表示し、3 秒後に外部画面へ遷移する
   */
  private async redirectForExpiredSession(sessionContext: SessionContext): Promise<never> {
    const expiresAtLabel = formatUtcDateTimeForDisplay(sessionContext.expiresAt);
    const delayMs = environment.auth.expiredSessionRedirectDelayMs;
    const message = expiresAtLabel
      ? `セッションの有効期限が切れています。（期限: ${expiresAtLabel}）${delayMs / 1000}秒後にトップ画面へ移動します。`
      : `セッションの有効期限が切れています。${delayMs / 1000}秒後にトップ画面へ移動します。`;
    console.error('[SessionAuthService] セッションの有効期限が切れています。', sessionContext);
    this.bootstrapStateSubject.next({ message, isError: true });
    this.clearStoredSessionContext();

    await this.redirectAfterDelay(environment.auth.expiredSessionRedirectDelayMs);

    throw new Error(message);
  }

  /** 設定に従って指定ミリ秒待機し、その後に遷移する */
  private async redirectAfterDelay(delayMs: number): Promise<void> {
    await new Promise<void>(resolve => {
      window.setTimeout(resolve, delayMs);
    });

    if (!environment.auth.redirectEnabled) {
      console.warn('[SessionAuthService] redirectEnabled=false のため自動遷移を停止しました。');
      return;
    }

    window.location.replace(environment.auth.invalidSessionRedirectUrl);
  }

  /**
   * 認証完了後に URL から session_key 系パラメータのみを除去する
   * 業務用の query parameter は維持し、ブラウザ履歴は増やさない
   */
  private cleanupSessionKeyFromUrl(): void {
    const currentUrl = new URL(window.location.href);
    let hasRemoved = false;

    for (const queryName of SessionAuthService.sessionKeyQueryNames) {
      if (!currentUrl.searchParams.has(queryName)) {
        continue;
      }

      currentUrl.searchParams.delete(queryName);
      hasRemoved = true;
    }

    if (currentUrl.searchParams.has(this.inspectionLinks.getAccessTokenQueryName())) {
      currentUrl.searchParams.delete(this.inspectionLinks.getAccessTokenQueryName());
      hasRemoved = true;
    }

    if (!hasRemoved) {
      return;
    }

    const normalizedQuery = currentUrl.searchParams.toString();
    const nextUrl = `${currentUrl.pathname}${normalizedQuery ? `?${normalizedQuery}` : ''}${currentUrl.hash}`;
    window.history.replaceState(window.history.state, document.title, nextUrl);
  }

  /** inspection 共有 URL の機微 query parameter を除去する */
  private cleanupInspectionSensitiveQueryParams(): void {
    const currentUrl = new URL(window.location.href);
    const nextSearch = this.inspectionLinks.cleanupSensitiveInspectionQueryParams(currentUrl.search);
    const nextUrl = `${currentUrl.pathname}${nextSearch}${currentUrl.hash}`;
    if (nextUrl === `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`) {
      return;
    }

    window.history.replaceState(window.history.state, document.title, nextUrl);
  }
}
